// _admin/AdminLayout.js
import React , { useState, useEffect  } from 'react';
import api from '../../../api/axios';
import NavbarAdmin from './NavbarAdmin';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useBanner } from '../_shared/hooks/useBanner';
import { useBankSettings } from '../_shared/hooks/useBankSettings';
import { useCompetitors } from '../_shared/hooks/useCompetitors';
import { useColors } from '../_shared/hooks/useColors';
import { useGallery } from '../_shared/hooks/useGallery';
import { useInfo } from '../_shared/hooks/useInfo';
import InfoEditor from './InfoEditor';
import { useLive } from '../_shared/hooks/useLive';
import { usePackages } from '../_shared/hooks/usePackages';
import { useRoutes } from '../_shared/hooks/useRoutes';
import { useSponsors } from '../_shared/hooks/useSponsors';
import AdminFormulario from '../../_forms/_admin/AdminFormulario';
import AdminAddCompetitorModal from './AdminAddCompetitorModal';
import AdminPreinscripciones from '../../admin/AdminPreinscripciones'; 
import KitManagement from '../_admin/KitManagement';
import { GpxImporter } from '../_shared/utils/GpxImporter';
import { useNotification } from '../../../errnotification/context/NotificationContext';
import { 
  FaImage, 
  FaPalette, 
  FaUniversity, 
  FaUsers, 
  FaImages, 
  FaClipboardList,
  FaInfoCircle, 
  FaClock, 
  FaBox, 
  FaRoute, 
  FaHandshake,
  FaFileAlt,
  FaBoxOpen
} from 'react-icons/fa';
import './AdminLayout.css';

const AdminLayout = ({ slug, templateData: initialTemplateData }) => {

  // ==========================================
  // ESTADO PARA SECCIÓN ACTIVA
  // ==========================================
  const [activeSection, setActiveSection] = useState('banner');
  const [templateData, setTemplateData] = useState(initialTemplateData || null);
  useEffect(() => {
    if (!initialTemplateData && slug) {
      const fetchData = async () => {
        try {
          const response = await api.get(`/admin/template/${slug}`);
          setTemplateData(response.data);
        } catch (error) {
          console.error('Error:', error);
        }
      };
      fetchData();
    }
  }, [slug, initialTemplateData]);

  // ==========================================
  // 0. DEFINIR isAdmin PRIMERO (antes que cualquier hook que lo use)
  // ==========================================
  const adminName = localStorage.getItem('name') || 'Administrador';
  const currentRole = localStorage.getItem('role');
  const isAdmin = currentRole === 'Administrador' || currentRole === 'Superadministrador';
  const isEditable = true;
  const handleTabChange = () => {};
  const { showNotification } = useNotification();
  const notifySuccess = (msg) => showNotification(msg, 'success');
  const notifyError = (msg) => showNotification(msg, 'error');
  // ==========================================
  // 1. BANNER - Hook
  // ==========================================
  const banner = useBanner({
    initialLogo: templateData?.banner?.logo || '',
    initialText: templateData?.banner?.text || '',
    initialBackground: templateData?.banner?.background || '',
    initialButtonText: 'Inscríbete',
    slug,
    isEditable: true,  // ← Importante: editable para admin
  });

  // ==========================================
  // 2. BANK SETTINGS - Hook
  // ==========================================
  const {
    banks,
    bankConfig,
    stripeConfig,
    useDefaultBank,
    useDefaultStripe,
    isBankEnabled,
    isStripeEnabled,
    isSavingBank,
    isSavingStripe,
    isTogglingBank,
    isTogglingStripe,
    testQR,
    testLoading,
    testError,
    stripeTest,
    setUseDefaultBank,
    setUseDefaultStripe,
    setBankConfig,
    setStripeConfig, 
    handleBankChange,
    handleStripeChange,
    handleSaveBank,
    handleSaveStripe,
    handleToggleBank,
    handleToggleStripe,
    handleTestQR,
    handleTestStripe
  } = useBankSettings({ slug, notifyError, notifySuccess });

    // ==========================================
  // 3. COLORS - Hook
  // ==========================================
  const {
    colors,
    loading: colorsLoading,
    saving: colorsSaving,
    handleColorChange,
    handleSaveColors,
    resetToDefaults,
  } = useColors(slug);


   // ==========================================
// 4. COMPETITORS - Hook
// ==========================================
const {
  competitors,
  filteredCompetitors,
  allColumns,
  visibleColumns,
  selectedColumns,
  formFields,
  isLoading,
  isSaving,
  isSavingColumns,
  isAdding,
  error,
  setError,
  successMessage,
  editingRow,
  editedData,
  showConfirm,
  setShowConfirm,
  confirmAction,
  competitorToDelete,
  publicListEnabled,
  isListOcultaPublic,
  searchTerm,
  itemsPerPage,
  currentPage,
  totalPages,
  startIndex,
  paginatedCompetitors,
  showAddModal,
  newCompetitor,
  setPublicListEnabled,
  setSearchTerm,
  setItemsPerPage,
  setCurrentPage,
  setShowAddModal,
  setNewCompetitor,
  setSelectedColumns,
  handleSaveColumns,
  handleTogglePublicList,
  handleAddCompetitor,
  handleEdit,
  handleSaveEdit,
  handleDelete,
  handleDownload,
  handleVisibleColumnChange,
  handleEditChange,
  cancelEdit,
  confirmActionHandler,
  handleConfirm,
  goToPage,
  fetchData,
  handleAddCompetitorConCampos,
} = useCompetitors(slug, isAdmin);

// ==========================================
// 5. GALLERY - Hook
// ==========================================
const {
  images: galleryImages,
  previewImages: galleryPreviews,
  selectedFiles: gallerySelectedFiles,
  isSaving: isGallerySaving,
  isEditing: isGalleryEditing,
  error: galleryError,
  handleFileChange: handleGalleryFileChange,
  handleUpload: handleGalleryUpload,
  handleEditToggle: handleGalleryEditToggle,
  cancelEdit: cancelGalleryEdit,
} = useGallery(slug, [], true);

// ==========================================
// 6. INFO SECTION - Hook
// ==========================================
const {
  content: infoContent,
  isEditing: isInfoEditing,
  isSaving: isInfoSaving,
  error: infoError,
  setContent: setInfoContent,
  handleSave: handleInfoSave,
  toggleEditing: toggleInfoEditing,
  cancelEdit: cancelInfoEdit,
} = useInfo(slug, true);

// ==========================================
// 7. LIVE TAB - Hook
// ==========================================
const {
  resultados: liveResultados,
  nuevaCategoria: liveNuevaCategoria,
  categoriasConfiguradas: liveCategoriasConfiguradas,
  pdfFile: livePdfFile,
  loading: liveLoading,
  mensaje: liveMensaje,
  vistaAdmin: liveVistaAdmin,
  isAdmin: liveIsAdmin,
  setNuevaCategoria: setLiveNuevaCategoria,
  setCategoriasConfiguradas: setLiveCategoriasConfiguradas,
  setPdfFile: setLivePdfFile,
  toggleVistaAdmin: toggleLiveVistaAdmin,
  handleAddCategoriaLocal: handleLiveAddCategoria,
  handleRemoveCategoriaLocal: handleLiveRemoveCategoria,
  handleGuardarCategoriasBD: handleLiveGuardarCategorias,
  handleUploadPdf: handleLiveUploadPdf,
  mostrarAlerta: mostrarLiveAlerta,
} = useLive(slug);

// ==========================================
// 8. PACKAGES - Hook
// ==========================================
const {
  packages: adminPackages,
  packageForms,
  selectedAddons: adminSelectedAddons,
  isSaving: isPackagesSaving,
  loading: packagesLoading,
  error: packagesError,
  handleSelectPackage: handleAdminSelectPackage,
  handleAddonToggle: handleAdminAddonToggle,
  handleAddAddon: handleAdminAddAddon,
  handleAddonChange: handleAdminAddonChange,
  handleDeleteAddon: handleAdminDeleteAddon,
  handleImageChange: handleAdminImageChange,
  handleSavePackages: handleAdminSavePackages,
  handleFormChange: handleAdminFormChange,
  handleIncludeChange: handleAdminIncludeChange,
  handleAddInclude: handleAdminAddInclude,
  handleDeleteInclude: handleAdminDeleteInclude,
  handleAddPackageForm: handleAdminAddPackageForm,
  handleDeletePackageForm: handleAdminDeletePackageForm,
  calculateTotalAmount: calculatePackageTotal,
  convertToUSD: convertPackageToUSD,
  formatDate: formatPackageDate,
  getCompetitorLabel: getPackageCompetitorLabel,
} = usePackages(slug, true);

// ==========================================
// 9. ROUTES - Hook
// ==========================================
const {
  routes: adminRoutes,
  isEditingRoutes: isEditingAdminRoutes,
  isSaving: isRoutesSaving,
  loading: routesLoading,
  error: routesError,
  handleRouteChange: handleAdminRouteChange,
  handleRouteImageChange: handleAdminRouteImageChange,
  handleAddRoute: handleAdminAddRoute,
  handleDeleteRoute: handleAdminDeleteRoute,
  handleRoutesSave: handleAdminRoutesSave,
  toggleEditing: toggleAdminRoutesEditing,
  cancelEdit: cancelAdminRoutesEdit,
} = useRoutes(slug, true);

// ==========================================
// 10. SPONSORS - Hook
// ==========================================
const {
  sponsors: adminSponsors,
  isEditing: isSponsorsEditing,
  loading: sponsorsLoading,
  error: sponsorsError,
  handleAddSponsors: handleAdminAddSponsors,
  handleDeleteSponsor: handleAdminDeleteSponsor,
  toggleEditing: toggleSponsorsEditing,
  getContinuousSponsors: getAdminContinuousSponsors,
  getDefaultSponsor: getAdminDefaultSponsor,
} = useSponsors(slug, [], true);



 // ==========================================
  // SECCIONES DEL MENÚ
  // ==========================================
  const menuSections = [
    { id: 'banner', icon: <FaImage />, label: 'Banner' },
    { id: 'colors', icon: <FaPalette />, label: 'Colores' },
    { id: 'bank', icon: <FaUniversity />, label: 'Banco' },
    { id: 'preinscripciones', icon: <FaClipboardList />, label: 'Preinscripciones' },
    { id: 'competitors', icon: <FaUsers />, label: 'Competidores' },
    { id: 'gallery', icon: <FaImages />, label: 'Galería' },
    { id: 'info', icon: <FaInfoCircle />, label: 'Información' },
    { id: 'live', icon: <FaClock />, label: 'Tiempos' },
    { id: 'packages', icon: <FaBox />, label: 'Paquetes' },
    { id: 'routes', icon: <FaRoute />, label: 'Rutas' },
    { id: 'sponsors', icon: <FaHandshake />, label: 'Patrocinadores' },
    { id: 'form', icon: <FaFileAlt />, label: 'Formulario' },
    { id: 'kits', icon: <FaBoxOpen />, label: 'Kits' },
  ];

  // ==========================================
  // RENDER: CONTENIDO SEGÚN SECCIÓN
  // ==========================================
  const renderContent = () => {
    switch (activeSection) {
      case 'banner':
        return renderBanner();
      case 'colors':
        return renderColorPicker();
      case 'bank':
        return renderBankSettings();
      case 'preinscripciones':
        return renderPreinscripciones();
      case 'competitors':
        return renderCompetitorsTab();
      case 'gallery':
        return renderGallery();
      case 'info':
        return renderInfo();
      case 'live':
        return renderLiveTab();
      case 'packages':
        return renderPackages();
      case 'routes':
        return renderRoutes();
      case 'sponsors':
        return renderSponsors();
        case 'form':  // ✅ CORREGIDO - Ahora renderiza directamente
        return (
          <div className="admin-section-content">
            <h2 className="admin-section-title">📝 Formulario</h2>
            <AdminFormulario 
              isEditable={true}
              selectedPackage={null}
              isFillingForm={false}
              onTabChange={handleTabChange} 
            />
          </div>
        );
        case 'kits':
        return renderKitsTab();
      default:
        return <div className="admin-empty">Selecciona una sección</div>;
    }
  };


// ==========================================
// 3. RENDER BANNER (Versión Corregida)
// ==========================================
const renderBanner = () => (
  <div className="admin-banner-wrapper">
    {/* Contenedor de la Portada Principal */}
    <div 
      className="admin-banner-cover"
      style={{ backgroundImage: banner.backgroundPreview ? `url(${banner.backgroundPreview})` : 'none' }}
    >
      <div className="admin-banner-overlay"></div>

      {/* Controles superiores (Editar / Guardar / Cancelar) */}
      <div className="admin-banner-controls">
        {banner.isEditing ? (
          <>
            <button 
              className="admin-btn admin-save-btn" 
              onClick={banner.handleSave} 
              disabled={banner.isSaving} 
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
              {banner.isSaving ? 'Guardando...' : 'Guardar'}
            </button>
            <button 
              className="admin-btn admin-cancel-btn" 
              onClick={banner.handleEditToggle} 
              disabled={banner.isSaving} 
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              Cancelar
            </button>
          </>
        ) : (
          <button 
            className="admin-btn admin-edit-btn" 
            onClick={banner.handleEditToggle} 
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            Editar Banner
          </button>
        )}
      </div>

      {/* Botón Flotante para cambiar la Portada */}
      {banner.isEditing && (
        <div className="admin-change-cover-overlay">
          <label className="admin-file-label" htmlFor="admin-bg-file">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
            Cambiar Portada
          </label>
          <input
            id="admin-bg-file"
            type="file"
            accept="image/*"
            className="admin-hidden-input"
            onChange={(e) => banner.handleFileChange(e, banner.setBackground, banner.setBackgroundPreview)}
          />
        </div>
      )}
    </div>

    {/* Bloque Inferior de Información y Logo */}
    <div className="admin-banner-info-container">
      <div className="admin-banner-profile-zone">
        <div className="admin-logo-holder">
          {banner.logoPreview ? (
            <img
              src={banner.logoPreview}
              alt="Logo del Evento"
              className="admin-banner-logo"
            />
          ) : (
            <div className="admin-logo-placeholder">Sin Logo</div>
          )}
          
          {banner.isEditing && (
            <div className="admin-change-logo-overlay">
              <label className="admin-file-label-mini" htmlFor="admin-logo-file" title="Cambiar Logo">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
              </label>
              <input
                id="admin-logo-file"
                type="file"
                accept="image/*"
                className="admin-hidden-input"
                onChange={(e) => banner.handleFileChange(e, banner.setLogo, banner.setLogoPreview)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="admin-banner-text-zone">
        {banner.isEditing ? (
          <div className="admin-edit-inputs-wrapper">
            <div className="admin-input-group">
              <label className="admin-field-label">Nombre del Evento</label>
              <input
                type="text"
                className="admin-banner-input"
                value={banner.text || ''}
                onChange={(e) => banner.setText(e.target.value)}
                placeholder="Ej. Gran Premio de Ciclismo 2026"
              />
            </div>

            <div className="admin-input-group">
              <label className="admin-field-label">Texto del Botón Principal</label>
              <input
                type="text"
                className="admin-banner-input"
                value={banner.buttonText || ''}
                onChange={(e) => banner.setButtonText(e.target.value)}
                placeholder="Ej. Inscribirme Ahora"
              />
            </div>
          </div>
        ) : (
          <div className="admin-banner-display-view">
            <h1 className="admin-banner-text">{banner.text || 'Nombre del Evento'}</h1>
            <button className="admin-banner-button" type="button">
              {banner.buttonText || 'Ver Más'}
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

    // ==========================================
  // 5. RENDER COLOR PICKER
  // ==========================================
  const renderColorPicker = () => {
    if (colorsLoading) {
      return (
        <div className="admin-color-picker-container">
          <p>Cargando colores...</p>
        </div>
      );
    }

    return (
      <div className="admin-color-picker-container">
        <h3>🎨 Selecciona los colores</h3>
        
        <div className="admin-color-picker__grid">
          <div className="admin-color-picker__field">
            <label>Color Primario:</label>
            <div className="admin-color-picker__input-wrapper">
              <input
                type="color"
                name="primaryColor"
                value={colors.primaryColor}
                onChange={handleColorChange}
              />
              <span className="admin-color-picker__hex">{colors.primaryColor}</span>
            </div>
          </div>

          <div className="admin-color-picker__field">
            <label>Color Secundario:</label>
            <div className="admin-color-picker__input-wrapper">
              <input
                type="color"
                name="secondaryColor"
                value={colors.secondaryColor}
                onChange={handleColorChange}
              />
              <span className="admin-color-picker__hex">{colors.secondaryColor}</span>
            </div>
          </div>

          <div className="admin-color-picker__field">
            <label>Color del Texto:</label>
            <div className="admin-color-picker__input-wrapper">
              <input
                type="color"
                name="textColor"
                value={colors.textColor}
                onChange={handleColorChange}
              />
              <span className="admin-color-picker__hex">{colors.textColor}</span>
            </div>
          </div>

          <div className="admin-color-picker__field">
            <label>Color de Fondo:</label>
            <div className="admin-color-picker__input-wrapper">
              <input
                type="color"
                name="backgroundColor"
                value={colors.backgroundColor}
                onChange={handleColorChange}
              />
              <span className="admin-color-picker__hex">{colors.backgroundColor}</span>
            </div>
          </div>
        </div>

        <div className="admin-color-picker__actions">
          <button 
            onClick={handleSaveColors} 
            className="admin-color-picker__save-btn"
            disabled={colorsSaving}
          >
            {colorsSaving ? 'Guardando...' : '💾 Guardar Colores'}
          </button>
          <button 
            onClick={resetToDefaults} 
            className="admin-color-picker__reset-btn"
            type="button"
          >
            🔄 Restaurar por defecto
          </button>
        </div>
      </div>
    );
  };

// ==========================================
  // 4. RENDER BANK SETTINGS
  // ==========================================
  const renderBankSettings = () => {
    // Encontrar el banco seleccionado para saber si necesita campos extra
    const selectedBank = banks.find(b => b.id === bankConfig.bank_id || b.id === Number(bankConfig.bank_id));
  
    return (
    <div className="admin-bank-settings">
      {/* Columna Principal */}
      <div className="admin-bank-settings__main">
        {/* Configuración del Banco */}
        <div className="admin-bank-settings__section">
          <h2>🏦 Configuración del Banco</h2>

          <label className="admin-bank-settings__checkbox">
            <input
              type="checkbox"
              checked={useDefaultBank}
              onChange={(e) => setUseDefaultBank(e.target.checked)}
            />
            Usar configuración por defecto
          </label>

          {/* Se muestra el formulario siempre que NO sea default */}
          {!useDefaultBank && (
            <div className="admin-bank-settings__form">
              <div className="admin-bank-settings__field">
                <label>Banco</label>
                <select
                  name="bank_id"
                  value={bankConfig.bank_id || ''}
                  onChange={handleBankChange}
                >
                  <option value="">Seleccione un banco</option>
                  {Array.isArray(banks) && banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                     {bank.name}
                   </option>
                  ))}
                </select>
              </div>

              <div className="admin-bank-settings__field">
                <label>Account ID</label>
                <input
                  type="text"
                  name="account_id"
                  value={bankConfig.account_id || ''}
                  onChange={handleBankChange}
                  placeholder="Ingrese el ID de cuenta"
                  autoComplete="off"
                />
              </div>

              <div className="admin-bank-settings__field">
                <label>Authorization ID</label>
                <input
                  type="password"
                  name="authorization_id"
                  value={bankConfig.authorization_id || ''}
                  onChange={handleBankChange}
                  placeholder="Ingrese el ID de autorización"
                  autoComplete="new-password"
                />
              </div>
              {/* 👈 SECCIÓN DINÁMICA PARA CREDENCIALES EXTRA (Futuro-proof) */}
                {selectedBank?.code === 'UNION' && (
                <div className="admin-bank-settings__field">
                  <label>Terminal ID (Específico de Banco Unión)</label>
                  <input
                    type="text"
                    value={bankConfig.extra_credentials?.terminal_id || ''}
                    onChange={(e) => setBankConfig(prev => ({
                      ...prev,
                      extra_credentials: { ...prev.extra_credentials, terminal_id: e.target.value }
                    }))}
                    placeholder="Ingrese el Terminal ID"
                    autoComplete="off"
                  />
                </div>
              )}
              
              {/* Puedes agregar más condiciones aquí para otros bancos: 
                  {selectedBank?.code === 'BISA' && ( ... )} 
              */}
            </div>
          )}

          <div className="admin-bank-settings__actions">
            <button
              type="button"
              onClick={handleToggleBank}
              className={`admin-bank-settings__toggle-btn ${isBankEnabled ? 'active' : 'inactive'}`}
              disabled={isTogglingBank}
            >
              {isTogglingBank
                ? 'Guardando...'
                : isBankEnabled
                ? 'Desactivar Banco'
                : 'Activar Banco'}
            </button>

            <button
              type="button"
              onClick={handleSaveBank}
              className="admin-bank-settings__save-btn"
              disabled={isSavingBank}
            >
              {isSavingBank ? 'Guardando...' : '💾 Guardar Configuración Banco'}
            </button>
          </div>
        </div>

        {/* Configuración de Stripe */}
        <div className="admin-bank-settings__section">
          <h2>💳 Configuración de Stripe</h2>

          <label className="admin-bank-settings__checkbox">
            <input
              type="checkbox"
              checked={useDefaultStripe}
              onChange={(e) => setUseDefaultStripe(e.target.checked)}
            />
            Usar configuración por defecto
          </label>

          {/* Se muestra el formulario siempre que NO sea default */}
          {!useDefaultStripe && (
            <div className="admin-bank-settings__form">
              <div className="admin-bank-settings__field">
                <label>Clave Secreta</label>
                <input
                  type="password"
                  name="secret_key"
                  value={stripeConfig.secret_key || ''}
                  onChange={handleStripeChange}
                  placeholder="Ingrese clave secreta"
                  autoComplete="new-password"
                />
              </div>
              <div className="admin-bank-settings__field">
                <label>Clave Pública</label>
                <input
                  type="text"
                  name="publishable_key"
                  value={stripeConfig.publishable_key || ''}
                  onChange={handleStripeChange}
                  placeholder="Ingrese clave pública"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <div className="admin-bank-settings__actions">
            <button
              type="button"
              onClick={handleToggleStripe}
              className={`admin-bank-settings__toggle-btn ${isStripeEnabled ? 'active' : 'inactive'}`}
              disabled={isTogglingStripe}
            >
              {isTogglingStripe
                ? 'Guardando...'
                : isStripeEnabled
                ? 'Desactivar Stripe'
                : 'Activar Stripe'}
            </button>

            <button
              type="button"
              onClick={handleSaveStripe}
              className="admin-bank-settings__save-btn"
            >
              💾 Guardar Configuración Stripe
            </button>
          </div>
        </div>
      </div>

      {/* Panel de pruebas (lado derecho) */}
      <div className="admin-bank-settings__test-panel">
        {/* Prueba Banco */}
        <div className="admin-bank-settings__test-section">
          <h3>🧪 Prueba Banco</h3>
          <p>Verifica la conexión con el banco</p>

          <button
            type="button"
            onClick={handleTestQR}
            className="admin-bank-settings__test-btn"
            disabled={testLoading}
          >
            {testLoading ? 'Probando...' : 'Generar QR de Prueba'}
          </button>

          {testError && (
            <div className="admin-bank-settings__test-error">
              ❌ Error: {testError}
            </div>
          )}

          {testQR && (
            <div className="admin-bank-settings__test-success">
              ✅ Conexión exitosa
              <img
                src={testQR}
                alt="QR de prueba"
                className="admin-bank-settings__test-qr"
              />
              <p className="admin-bank-settings__test-note">
                QR generado correctamente
              </p>
            </div>
          )}
        </div>

        {/* Prueba Stripe */}
        <div className="admin-bank-settings__test-section">
          <h3>🧪 Prueba Stripe</h3>
          <p>Verifica la conexión con Stripe</p>

          <button
            type="button"
            onClick={handleTestStripe}
            className="admin-bank-settings__test-btn"
            disabled={stripeTest.loading}
          >
            {stripeTest.loading ? 'Probando...' : 'Probar Conexión Stripe'}
          </button>

          {stripeTest.error && (
            <div className="admin-bank-settings__test-error">
              ❌ Error: {stripeTest.error}
            </div>
          )}

          {stripeTest.success && (
            <div className="admin-bank-settings__test-success">
              ✅ Conexión exitosa
              <p className="admin-bank-settings__test-note">
                Stripe respondió correctamente
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };
// ==========================================
// RENDER PREINSCRIPCIONES
// ==========================================
const renderPreinscripciones = () => {
  return (
    <div className="admin-section-content">
      <h2 className="admin-section-title">📋 Gestión de Preinscripciones</h2>
      <AdminPreinscripciones userSlug={slug} />
    </div>
  );
};



  // ==========================================
// RENDER COMPETITORS TAB
// ==========================================
const renderCompetitorsTab = () => {
  // Si no es admin y la lista está oculta
  if (!isAdmin && isListOcultaPublic) {
    return (
      <div className="admin-competitors-tab">
        <h2 className="admin-competitors-title">Lista de Inscritos</h2>
        <div className="admin-competitors-private">
          <span className="admin-competitors-lock">🔒</span>
          <h3>Lista de Participantes Privada</h3>
          <p>Por disposición de los organizadores del evento, el listado público no se encuentra disponible.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-competitors-tab">
      <h2 className="admin-competitors-title">
        Lista de Inscritos 
        {!publicListEnabled && isAdmin && (
          <span className="admin-competitors-hidden-badge">(Vista Oculta al Público)</span>
        )}
      </h2>

      {successMessage && (
        <div className="admin-competitors-success">
          {successMessage}
        </div>
      )}

      {/* Barra de búsqueda */}
      <div className="admin-competitors-search">
        <div className="admin-competitors-search-container">
          <span className="admin-competitors-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Buscar inscritos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-competitors-search-input"
          />
        </div>
      </div>

      {/* Configuración de visibilidad - Solo admin */}
      {isAdmin && (
        <div className="admin-competitors-section">
          <h3 className="admin-competitors-subtitle">Configuración General de Visibilidad</h3>
          
          <div className="admin-competitors-visibility-toggle">
          <input
  type="checkbox"
  id="toggle-public-list"
  checked={publicListEnabled}
  onChange={(e) => handleTogglePublicList(e.target.checked)} // ✅ Ahora guarda al instante
  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
  disabled={isSavingColumns} // Evita doble clic mientras guarda
/>
            <label htmlFor="toggle-public-list">
              {publicListEnabled ? (
                <>
                  <span className="admin-competitors-eye-open">👁️</span>
                  <span>Lista pública activa (La pestaña es visible en la web)</span>
                </>
              ) : (
                <>
                  <span className="admin-competitors-eye-closed">🚫</span>
                  <span>Lista oculta al público (Solo visible para Administradores)</span>
                </>
              )}
            </label>
          </div>

          <h4 className="admin-competitors-columns-label">Columnas visibles en la vista pública:</h4>
          <p className="admin-competitors-columns-help">Selecciona los datos específicos que deseas que el competidor promedio pueda ver si la lista es pública:</p>
          
          <div className="admin-competitors-checkbox-grid">
            {allColumns.map(column => (
              <label key={`visible-${column}`} className="admin-competitors-checkbox-label">
                <input
                  type="checkbox"
                  checked={visibleColumns.includes(column)}
                  onChange={() => handleVisibleColumnChange(column)}
                />
                <span>{column}</span>
              </label>
            ))}
          </div>

          <button
            onClick={handleSaveColumns}
            className="admin-competitors-action-btn"
            disabled={isSavingColumns}
          >
            {isSavingColumns ? 'Guardando...' : 'Guardar configuración'}
          </button>
        </div>
      )}

      {/* Selección de columnas para descarga - Solo admin */}
      {isAdmin && (
        <div className="admin-competitors-section">
          <h3 className="admin-competitors-subtitle">Selecciona las columnas a descargar</h3>
          <div className="admin-competitors-checkbox-grid">
            {allColumns.map(column => (
              <label key={`download-${column}`} className="admin-competitors-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(column)}
                  onChange={() =>
                    setSelectedColumns(prev =>
                      prev.includes(column)
                        ? prev.filter(col => col !== column)
                        : [...prev, column]
                    )
                  }
                />
                <span>{column}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Tabla de competidores */}
      <div className="admin-competitors-table-container">
        {isLoading ? (
          <div className="admin-competitors-loading">Cargando datos...</div>
        ) : (
          <>
            <table className="admin-competitors-table">
              <thead>
                <tr>
                  {visibleColumns.map(column => (
                    <th key={column}>{column}</th>
                  ))}
                  {isAdmin && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedCompetitors.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + (isAdmin ? 1 : 0)}>
                      {searchTerm ? 'No se encontraron resultados' : 'No hay registros'}
                    </td>
                  </tr>
                ) : (
                  paginatedCompetitors.map(comp => (
                    <tr key={comp.id}>
                      {visibleColumns.map(column => (
                        <td key={column}>
                          {editingRow === comp.id ? (
                            <input
                              type="text"
                              value={editedData[column] || ''}
                              onChange={(e) => handleEditChange(column, e.target.value)}
                              className="admin-competitors-edit-input"
                            />
                          ) : (
                            comp[column]
                          )}
                        </td>
                      ))}
                      {isAdmin && (
                        <td className="admin-competitors-actions">
                          {editingRow === comp.id ? (
                            <>
                              <button
                                onClick={() => confirmActionHandler("save")}
                                className="admin-competitors-action-btn admin-competitors-save-btn"
                              >
                                Guardar
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="admin-competitors-action-btn admin-competitors-cancel-btn"
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="admin-competitors-action-btn admin-competitors-edit-btn"
                                onClick={() => handleEdit(comp)}
                              >
                                ✏️
                              </button>
                              <button
                                className="admin-competitors-action-btn admin-competitors-delete-btn"
                                onClick={() => confirmActionHandler("delete", comp.id)}
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Paginación */}
            {filteredCompetitors.length > 0 && (
              <div className="admin-competitors-pagination">
                <div className="admin-competitors-pagination-info">
                  Mostrando {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredCompetitors.length)} de {filteredCompetitors.length} registros
                </div>

                <div className="admin-competitors-pagination-buttons">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="admin-competitors-page-selector"
                  >
                    <option value="5">5 por página</option>
                    <option value="10">10 por página</option>
                    <option value="20">20 por página</option>
                    <option value="50">50 por página</option>
                  </select>

                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="admin-competitors-page-btn"
                  >
                    ◀
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`admin-competitors-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="admin-competitors-page-btn"
                  >
                    ▶
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Botones de acción - Solo admin */}
      {isAdmin && (
        <div className="admin-competitors-actions-bar">
          <button
            className="admin-competitors-download-btn"
            onClick={handleDownload}
            disabled={competitors.length === 0 || isLoading}
          >
            ⬇️ Descargar Seleccionados
          </button>
          <button
            className="admin-competitors-add-btn"
            onClick={() => setShowAddModal(true)}
            disabled={isLoading}
          >
            ➕ Agregar Corredor
          </button>
        </div>
      )}

      {/* Modal agregar */}
      {showAddModal && (
  <AdminAddCompetitorModal
    slug={slug}
    packages={adminPackages}
    onClose={() => setShowAddModal(false)}
    onSave={handleAddCompetitorConCampos}
    isSaving={isAdding}
  />
)}

      {/* Modal confirmación */}
      {showConfirm && (
        <div className="admin-competitors-modal-overlay">
          <div className="admin-competitors-modal admin-competitors-confirm-modal">
            <p>
              {confirmAction === "save"
                ? "¿Estás seguro de que deseas guardar los cambios?"
                : "¿Estás seguro de que deseas eliminar este competidor?"}
            </p>
            <div className="admin-competitors-modal-actions">
              <button onClick={handleConfirm}>Sí, continuar</button>
              <button onClick={() => setShowConfirm(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="admin-competitors-error">
          {error}
          <button onClick={() => setError(null)}>Cerrar</button>
        </div>
      )}
    </div>
  );
};

// ==========================================
// RENDER GALLERY
// ==========================================
const renderGallery = () => (
  <div className="admin-gallery-container">
    <h3 className="admin-gallery-title">🖼️ Galería</h3>
    
    {galleryError && (
      <div className="admin-gallery-error">{galleryError}</div>
    )}

    {/* Botón de edición */}
    <button 
      className="admin-gallery-edit-btn" 
      onClick={handleGalleryEditToggle}
    >
      {isGalleryEditing ? "❌ Cancelar Edición" : "✏️ Editar Galería"}
    </button>

    {/* Panel de carga */}
    {isGalleryEditing && (
      <div className="admin-gallery-upload-area">
        <div className="admin-gallery-drop-zone">
          <input 
            type="file" 
            multiple 
            accept="image/*" 
            onChange={handleGalleryFileChange} 
            disabled={isGallerySaving}
            className="admin-gallery-file-input"
          />
          <p className="admin-gallery-drop-text">
            {isGallerySaving ? 'Subiendo...' : 'Arrastra o haz clic para seleccionar imágenes'}
          </p>
          <small className="admin-gallery-drop-hint">
            Máximo 5MB por imagen
          </small>
        </div>

        {/* Previews */}
        {galleryPreviews.length > 0 && (
          <div className="admin-gallery-previews">
            {galleryPreviews.map(({ preview }, index) => (
              <div key={index} className="admin-gallery-preview-item">
                <img 
                  src={preview} 
                  alt={`Vista previa ${index + 1}`} 
                  className="admin-gallery-preview-img" 
                />
              </div>
            ))}
          </div>
        )}

        {/* Botón de subida */}
        <div className="admin-gallery-upload-actions">
          <button 
            className="admin-gallery-upload-btn" 
            onClick={handleGalleryUpload} 
            disabled={isGallerySaving || gallerySelectedFiles.length === 0}
          >
            {isGallerySaving ? "⏳ Subiendo..." : "📤 Subir Imágenes"}
          </button>
          {gallerySelectedFiles.length > 0 && (
            <span className="admin-gallery-file-count">
              {gallerySelectedFiles.length} imagen(es) seleccionada(s)
            </span>
          )}
        </div>
      </div>
    )}

    {/* Mosaico de imágenes */}
    {galleryImages.length > 0 ? (
      <div className="admin-gallery-grid">
        {galleryImages.map((src, index) => (
          <div key={index} className="admin-gallery-item">
            <img 
              src={src} 
              alt={`Imagen ${index + 1}`} 
              className="admin-gallery-image" 
            />
          </div>
        ))}
      </div>
    ) : (
      <div className="admin-gallery-empty">
        <p>No hay imágenes en la galería</p>
      </div>
    )}
  </div>
);

// ==========================================
// RENDER INFO SECTION
// ==========================================
const renderInfo = () => {
  return <InfoEditor slug={slug} />;
};

// ==========================================
// RENDER LIVE TAB
// ==========================================
const renderLiveTab = () => (
  <div className="admin-live-container">
    {/* Encabezado Dinámico para Admin */}
    <div className="admin-live-header">
      <h2 className="admin-live-title">⏱️ Tiempos y Clasificación Oficial</h2>
      {liveIsAdmin && (
        <button 
          onClick={toggleLiveVistaAdmin} 
          className={`admin-live-toggle-btn ${liveVistaAdmin ? 'active' : ''}`}
        >
          {liveVistaAdmin ? "👁️ Ver Tabla Pública" : "⚙️ Configurar e Importar (Admin)"}
        </button>
      )}
    </div>

    {/* Banner de Mensajes / Alertas */}
    {liveMensaje.texto && (
      <div className={`admin-live-alert admin-live-alert--${liveMensaje.tipo}`}>
        {liveMensaje.texto}
      </div>
    )}

    {/* VISTA ADMINISTRADOR */}
    {liveIsAdmin && liveVistaAdmin ? (
      <div className="admin-live-grid">
        {/* Bloque 1: Configurar Categorías */}
        <div className="admin-live-card">
          <h3 className="admin-live-card-title">1. Definir Categorías del Evento</h3>
          <p className="admin-live-card-subtitle">
            Escribe las categorías exactamente como aparecen impresas en tu encabezado de Race Result.
          </p>
          
          <form onSubmit={handleLiveAddCategoria} className="admin-live-form-inline">
            <input 
              type="text" 
              placeholder="Ej. JUVENIL A DAMAS" 
              value={liveNuevaCategoria}
              onChange={(e) => setLiveNuevaCategoria(e.target.value)}
              className="admin-live-input"
            />
            <button type="submit" className="admin-live-btn admin-live-btn-primary">
              Añadir
            </button>
          </form>

          <div className="admin-live-tags">
            {liveCategoriasConfiguradas.map((cat, index) => (
              <span key={index} className="admin-live-tag">
                {cat} 
                <button 
                  type="button" 
                  onClick={() => handleLiveRemoveCategoria(index)} 
                  className="admin-live-tag-delete"
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <button 
            onClick={handleLiveGuardarCategorias} 
            disabled={liveLoading} 
            className="admin-live-btn admin-live-btn-success admin-live-btn-full"
          >
            {liveLoading ? "Guardando..." : "🔒 Fijar Categorías en Servidor"}
          </button>
        </div>

        {/* Bloque 2: Subir PDF */}
        <div className="admin-live-card">
          <h3 className="admin-live-card-title">2. Cargar Reporte de Tiempos</h3>
          <p className="admin-live-card-subtitle">
            Sube el PDF oficial generado. El sistema lo cruzará con las categorías fijadas en el paso 1.
          </p>
          
          <form onSubmit={handleLiveUploadPdf} className="admin-live-form-upload">
            <div className="admin-live-dropzone">
              <input 
                type="file" 
                accept=".pdf" 
                id="admin-live-pdf-input" 
                onChange={(e) => setLivePdfFile(e.target.files[0])}
                className="admin-live-file-input"
              />
              <label htmlFor="admin-live-pdf-input" className="admin-live-file-label">
                {livePdfFile ? `📄 ${livePdfFile.name}` : "📁 Haz clic para seleccionar el PDF"}
              </label>
            </div>
            <button 
              type="submit" 
              disabled={liveLoading || !livePdfFile} 
              className={`admin-live-btn ${(!livePdfFile || liveLoading) ? 'admin-live-btn-disabled' : 'admin-live-btn-success'}`}
            >
              {liveLoading ? "Procesando Documento..." : "🚀 Publicar Clasificación en Vivo"}
            </button>
          </form>
        </div>
      </div>
    ) : (
      /* VISTA PÚBLICA (TABLA DE RESULTADOS) */
      <div className="admin-live-table-wrapper">
        {(!liveResultados || !Array.isArray(liveResultados) || liveResultados.length === 0) ? (
          <div className="admin-live-no-data">
            <p className="admin-live-no-data-text">
              Los tiempos oficiales se publicarán en cuanto el juez del evento suba el reporte de clasificación.
            </p>
          </div>
        ) : (
          <div className="admin-live-table-responsive">
            <table className="admin-live-table">
              <thead>
                <tr className="admin-live-table-header">
                  <th className="admin-live-th">Pos.</th>
                  <th className="admin-live-th">Dorsal</th>
                  <th className="admin-live-th">Competidor</th>
                  <th className="admin-live-th">Categoría Oficial</th>
                  <th className="admin-live-th admin-live-th-right">Tiempo</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(liveResultados) && liveResultados.map((runner, index) => (
                  <tr key={runner.id || index} className={index % 2 === 0 ? 'admin-live-row-even' : 'admin-live-row-odd'}>
                    <td className="admin-live-td admin-live-td-pos">#{runner.posicion}</td>
                    <td className="admin-live-td admin-live-td-dorsal">{runner.dorsal}</td>
                    <td className="admin-live-td admin-live-td-nombre">{runner.nombre}</td>
                    <td className="admin-live-td admin-live-td-categoria">{runner.categoria_nombre}</td>
                    <td className="admin-live-td admin-live-td-tiempo">{runner.tiempo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )}
  </div>
);


// ==========================================
// RENDER PACKAGES (ADMIN)
// ==========================================
const renderPackages = () => (
  <div className="admin-packages-container">
    <h2 className="admin-packages-title" style={{ color: colors?.primaryColor || '#9370DB' }}>
      📦 ELIGE TU INSCRIPCIÓN
    </h2>

    {packagesLoading ? (
      <div className="admin-packages-loading">Cargando paquetes...</div>
    ) : (
      <>
        {/* Vista de paquetes públicos (preview) */}
        <div className="admin-packages-grid">
          {adminPackages.map((pkg, index) => {
            const totalAmount = calculatePackageTotal(pkg);
            const usdEquivalent = convertPackageToUSD(pkg, totalAmount);
            const isClosed = pkg.is_closed;
            const numberOfCompetitors = parseInt(pkg.number_of_competitors) || 1;
            const amountPerPerson = parseFloat(pkg.amount) || 0;
            const isMultipleCompetitors = numberOfCompetitors > 1;

            return (
              <div 
                key={pkg.id_package || index} 
                className={`admin-package-card ${index === 1 ? 'admin-package-card--featured' : ''} ${isClosed ? 'admin-package-card--closed' : ''}`}
                style={{ '--accent-color': colors?.primaryColor || '#9370DB' }}
              >
                {isClosed && (
                  <div className="admin-package-overlay">
                    <span className="admin-package-overlay-text">INSCRIPCIONES CERRADAS</span>
                  </div>
                )}

                {pkg.image && (
                  <div className="admin-package-image-container">
                    <img 
                      src={pkg.image} 
                      alt={pkg.title} 
                      className="admin-package-image"
                    />
                  </div>
                )}

                <span className="admin-package-badge">
                  {getPackageCompetitorLabel(numberOfCompetitors)}
                </span>

                <div className="admin-package-content">
                  <div className="admin-package-header">
                    <h3 className="admin-package-title">{pkg.title}</h3>
                    <p className="admin-package-desc">{pkg.description}</p>
                  </div>

                  <div className="admin-package-price-zone">
                    <div className="admin-package-price-main">
                      <span className="admin-package-currency">{pkg.currency}</span>
                      <span className="admin-package-amount">{totalAmount}</span>
                    </div>
                    <div className="admin-package-price-usd">~ ${usdEquivalent} USD</div>
                    {isMultipleCompetitors && (
                      <div className="admin-package-price-per-person">
                        <small>{amountPerPerson} {pkg.currency} por persona</small>
                      </div>
                    )}
                  </div>

                  {/* Addons - solo para preview */}
                  {(pkg.addons || []).length > 0 && (
                    <div className="admin-package-addons">
                      <strong className="admin-package-addons-title">✦ OPCIONES ADICIONALES</strong>
                      {pkg.addons.map(addon => {
                        const addonExtraAmount = parseFloat(addon.extra_amount) || 0;
                        return (
                          <label key={addon.id} className="admin-package-addon-option">
                            <input
                              type="checkbox"
                              checked={!!adminSelectedAddons[pkg.id_package]?.[addon.id]}
                              onChange={() => handleAdminAddonToggle(pkg.id_package, addon.id)}
                              disabled={isClosed}
                            />
                            <span className="admin-package-addon-label">{addon.label}</span>
                            <span className="admin-package-addon-price">+{addonExtraAmount} {pkg.currency}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <div className="admin-package-body">
                    <ul className="admin-package-includes">
                      {pkg.includes.map((inc) => (
                        <li key={inc.id} className="admin-package-include-item">
                          <span className="admin-package-check-icon">✓</span> 
                          {inc.label} 
                          {inc.required && <small className="admin-package-req-tag">(Obligatorio)</small>}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="admin-package-dates">
                    <p><strong>Inicio:</strong> {formatPackageDate(pkg.startDate)}</p>
                    <p><strong>Fin:</strong> {formatPackageDate(pkg.endDate)}</p>
                  </div>

                  <div className="admin-package-footer">
                    <button 
                      className="admin-package-select-btn"
                      disabled={isClosed}
                      style={{ 
                        backgroundColor: isClosed ? '#444' : (colors?.primaryColor || '#9370DB'),
                      }}
                    >
                      {isClosed ? 'No Disponible' : 'Seleccionar'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Panel de Edición */}
        <div className="admin-packages-editor">
          <h3 className="admin-packages-editor-title">⚙️ Panel de Control</h3>
          
          {packageForms.map((form, index) => (
            <div key={form.id_package || `form-${index}`} className="admin-package-edit-card">
              <div className="admin-package-edit-header">
                <h4>
                  Paquete #{index + 1} 
                  {form.id_package ? ` (ID: ${form.id_package})` : ' (Nuevo)'}
                </h4>
                
                <label className="admin-package-switch">
                  <input
                    type="checkbox"
                    checked={form.is_closed}
                    onChange={(e) => handleAdminFormChange(index, 'is_closed', e.target.checked)}
                  />
                  <span className="admin-package-switch-label">🚨 CERRAR INSCRIPCIONES</span>
                </label>
              </div>

              {/* Imagen */}
              <div className="admin-package-image-upload">
                <label>Imagen del paquete:</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleAdminImageChange(index, e.target.files[0])}
                />
                {form.imagePreview && (
                  <div className="admin-package-image-preview">
                    <img src={form.imagePreview} alt="Vista previa" />
                  </div>
                )}
                {form.image && !form.imagePreview && (
                  <div className="admin-package-image-preview">
                    <img src={form.image} alt="Imagen actual" />
                    <small>Imagen actual</small>
                  </div>
                )}
              </div>

              {/* Campos */}
              <div className="admin-package-fields-grid">
                <label>
                  Título:
                  <input 
                    type="text" 
                    value={form.title} 
                    onChange={(e) => handleAdminFormChange(index, 'title', e.target.value)} 
                  />
                </label>
                <label>
                  Descripción:
                  <input 
                    type="text" 
                    value={form.description || ''} 
                    onChange={(e) => handleAdminFormChange(index, 'description', e.target.value)} 
                  />
                </label>
                <label>
                  Precio por persona:
                  <input 
                    type="number" 
                    value={form.amount} 
                    onChange={(e) => handleAdminFormChange(index, 'amount', parseFloat(e.target.value) || 0)} 
                  />
                </label>
                <label>
                  Competidores:
                  <select 
                    value={form.number_of_competitors} 
                    onChange={(e) => handleAdminFormChange(index, 'number_of_competitors', parseInt(e.target.value))}
                  >
                    <option value="1">1 - Individual</option>
                    <option value="2">2 - Duplas</option>
                    <option value="3">3 - Tríos</option>
                    <option value="4">4 - Equipos de 4</option>
                    <option value="5">5 - Equipos de 5</option>
                    <option value="6">6 - Equipos de 6</option>
                  </select>
                </label>
                <label>
                  Conversión Bs/USD:
                  <input 
                    type="number" 
                    step="0.01" 
                    value={form.conversion_rate} 
                    onChange={(e) => handleAdminFormChange(index, 'conversion_rate', parseFloat(e.target.value) || 6.96)} 
                  />
                </label>
                <label>
                  Fecha Inicio:
                  <input 
                    type="date" 
                    value={form.startDate} 
                    onChange={(e) => handleAdminFormChange(index, 'startDate', e.target.value)} 
                  />
                </label>
                <label>
                  Fecha Fin:
                  <input 
                    type="date" 
                    value={form.endDate} 
                    onChange={(e) => handleAdminFormChange(index, 'endDate', e.target.value)} 
                  />
                </label>
              </div>

              {/* Includes */}
              <div className="admin-package-includes-zone">
                <h5>Elementos Incluidos:</h5>
                {form.includes.map((include, incIndex) => (
                  <div key={include.id} className="admin-package-include-row">
                    <input
                      type="text"
                      placeholder="Ej: Polera Oficial"
                      value={include.label}
                      onChange={(e) => handleAdminIncludeChange(index, incIndex, 'label', e.target.value)}
                    />
                    <label>
                      <input
                        type="checkbox"
                        checked={include.required}
                        onChange={(e) => handleAdminIncludeChange(index, incIndex, 'required', e.target.checked)}
                      /> Obligatorio
                    </label>
                    <button 
                      type="button" 
                      className="admin-package-delete-include"
                      onClick={() => handleAdminDeleteInclude(index, incIndex)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button 
                  type="button" 
                  className="admin-package-add-include"
                  onClick={() => handleAdminAddInclude(index)}
                >
                  + Añadir Ítem
                </button>
              </div>

              {/* Addons */}
              <div className="admin-package-addons-zone">
                <h5>✦ Opciones Adicionales (Addons):</h5>
                {form.addons?.map((addon, addonIndex) => (
                  <div key={addonIndex} className="admin-package-addon-row">
                    <input
                      placeholder="ID (ej: polera)"
                      value={addon.id}
                      onChange={(e) => handleAdminAddonChange(index, addonIndex, 'id', e.target.value)}
                    />
                    <input
                      placeholder="Etiqueta (ej: Con polera)"
                      value={addon.label}
                      onChange={(e) => handleAdminAddonChange(index, addonIndex, 'label', e.target.value)}
                    />
                    <input
                      type="number"
                      placeholder="Monto extra"
                      value={addon.extra_amount}
                      onChange={(e) => handleAdminAddonChange(index, addonIndex, 'extra_amount', parseFloat(e.target.value) || 0)}
                    />
                    <button 
                      type="button" 
                      className="admin-package-delete-addon"
                      onClick={() => handleAdminDeleteAddon(index, addonIndex)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button 
                  type="button" 
                  className="admin-package-add-addon"
                  onClick={() => handleAdminAddAddon(index)}
                >
                  + Agregar opción adicional
                </button>
              </div>

              <button 
                type="button" 
                className="admin-package-delete-btn"
                onClick={() => handleAdminDeletePackageForm(index)}
              >
                🗑️ Eliminar paquete
              </button>
            </div>
          ))}

          <div className="admin-package-actions-bar">
            <button 
              type="button" 
              className="admin-package-create-btn"
              onClick={handleAdminAddPackageForm}
            >
              + Crear Nuevo Paquete
            </button>
            <button 
              type="button" 
              className="admin-package-save-btn"
              onClick={handleAdminSavePackages}
              disabled={isPackagesSaving}
            >
              {isPackagesSaving ? 'Guardando...' : '💾 Guardar Cambios'}
            </button>
          </div>
        </div>
      </>
    )}
  </div>
);

// ==========================================
// RENDER ROUTES
// ==========================================
const renderRoutes = () => {
  if (routesLoading) {
    return (
      <div className="admin-routes-loading">
        <p>Cargando rutas...</p>
      </div>
    );
  }



  return (
    <div className="admin-routes-section">
      <h2 className="admin-routes-title">🗺️ Rutas del Evento</h2>

      {routesError && (
        <div className="admin-routes-error">{routesError}</div>
      )}

      {isEditingAdminRoutes ? (
        // MODO EDICIÓN
        <div className="admin-routes-edit-container">
          {adminRoutes.map((route, index) => (
            <div key={index} className="admin-route-item">
              <div className="admin-route-content">
                <h3 className="admin-route-edit-title">Editar Ruta {index + 1}</h3>
                
                <input
                  type="text"
                  value={route.title}
                  onChange={(e) => handleAdminRouteChange(index, 'title', e.target.value)}
                  placeholder="Título de la ruta (Ej: 10K Terrellas)"
                  className="admin-route-title-input"
                />
                
                <textarea
                  value={route.description}
                  onChange={(e) => handleAdminRouteChange(index, 'description', e.target.value)}
                  placeholder="Descripción detallada de la ruta..."
                  rows="4"
                  className="admin-route-textarea"
                />
                
                <input
                  type="text"
                  value={route.link}
                  onChange={(e) => handleAdminRouteChange(index, 'link', e.target.value)}
                  placeholder="Enlace de Strava o mapa"
                  className="admin-route-link-input"
                />

                {/* 🛠️ NUEVO: CAMPO DE TEXTO PARA EL JSON DEL MAPA */}
                <div className="admin-route-json-block" style={{ marginTop: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontWeight: 'bold', color: '#bc00dd', fontSize: '13px', display: 'block' }}>
                      🌐 Configuración del Mapa Interactivo (JSON)
                    </label>
                    <button
                      onClick={() => {
                        const template = `{
                  "start": [-16.500000, -68.150000],
                  "end": [-16.510000, -68.160000],
                  "path": [
                    [-16.500000, -68.150000],
                    [-16.505000, -68.155000],
                    [-16.510000, -68.160000]
                  ],
                  "elevation_gain": 150,
                  "elevation_loss": 145,
                  "terrain": "Asfalto y Tierra",
                  "est_time": "1h 15min"
                }`;
                        navigator.clipboard.writeText(template);
                        alert('✅ Ejemplo copiado al portapapeles. Pégalo en el campo y modifica los valores.');
                      }}
                      style={{
                        background: '#bc00dd',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 14px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      📋 Copiar Ejemplo
                    </button>
                  </div>

                  <GpxImporter 
                      index={index} 
                      onDataParsed={(jsonData) => {
                        handleAdminRouteChange(index, 'map_data', jsonData);
                      }} 
                    />
                    
                    <textarea
                      value={route.map_data || ''}
                      onChange={(e) => handleAdminRouteChange(index, 'map_data', e.target.value)}
                      placeholder={`{
                        "start": [-16.500000, -68.150000],
                        "end": [-16.510000, -68.160000],
                        "path": [
                          [-16.500000, -68.150000],
                          [-16.505000, -68.155000],
                          [-16.510000, -68.160000]
                        ],
                        "elevation_gain": 150,
                        "elevation_loss": 145,
                        "terrain": "Asfalto y Tierra",
                        "est_time": "1h 15min"
                      }`}
                      rows="10"
                      style={{
                        width: '100%',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        backgroundColor: '#0a0a0a',
                        color: '#00E676',
                        border: '1px solid #333',
                        borderRadius: '6px',
                        padding: '10px',
                        lineHeight: '1.4'
                      }}
                    />
                    
                    <small style={{ color: '#888', display: 'block', marginTop: '6px', lineHeight: '1.4' }}>
                      💡 <b>Instrucciones:</b> Copia el formato del ejemplo. Modifica solo los números de latitud y longitud. 
                      <br/>⚠️ Usa siempre <b>comillas dobles ("")</b> para los textos y <b>no olvides las comas</b> al final de cada línea (excepto la última).
                    </small>
                  </div>
              </div>
              
              <div className="admin-route-media">
                <input 
                  type="file" 
                  onChange={(e) => handleAdminRouteImageChange(e, index)} 
                  id={`admin-route-image-${index}`}
                  className="admin-route-file-input"
                />
                <label htmlFor={`admin-route-image-${index}`} className="admin-route-upload-btn">
                  📤 Subir Altimetría
                </label>
                {route.image && (
                  <img
                    src={route.image}
                    alt={`Altimetría Ruta ${index + 1}`}
                    className="admin-route-image"
                  />
                )}
                <button 
                  onClick={() => handleAdminDeleteRoute(index)}
                  className="admin-route-delete-btn"
                >
                  🗑️ Eliminar Ruta
                </button>
              </div>
            </div>
          ))}
          
          <div className="admin-routes-controls">
            <button 
              onClick={handleAdminAddRoute} 
              className="admin-routes-add-btn"
              disabled={adminRoutes.length >= 6}
            >
              + Añadir Ruta
            </button>
            <button 
              onClick={handleAdminRoutesSave} 
              disabled={isRoutesSaving} 
              className="admin-routes-save-btn"
            >
              {isRoutesSaving ? 'Guardando...' : '💾 Guardar Rutas'}
            </button>
            <button 
              onClick={cancelAdminRoutesEdit} 
              className="admin-routes-cancel-btn"
            >
              ❌ Cancelar
            </button>
          </div>
        </div>
      ) : (
        // MODO VISUALIZACIÓN
        <div className="admin-routes-display">
          {adminRoutes.length === 0 ? (
            <div className="admin-routes-empty">
              <p>No hay rutas configuradas.</p>
            </div>
          ) : (
            adminRoutes.map((route, index) => {
              // Parseamos temporalmente para renderizar un preview en la vista de administración
              let parsedMap = null;
              if (route.map_data) {
                try {
                  parsedMap = typeof route.map_data === 'string' ? JSON.parse(route.map_data) : route.map_data;
                } catch(e) {
                  parsedMap = null;
                }
              }

              return (
                <div key={index} className="admin-route-item">
                  <div className="admin-route-content">
                    <h3 className="admin-route-title">{route.title || `Ruta ${index + 1}`}</h3>
                    <p className="admin-route-description">{route.description}</p>
                    
                    {/* 🛠️ NUEVO: FICHA RESUMEN DEL MAPA CONFIGURADO */}
                    {parsedMap && (
                      <div className="admin-route-map-preview-badge" style={{
                        marginTop: '12px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px dashed rgba(255, 255, 255, 0.1)'
                      }}>
                        <span style={{ fontSize: '11px', color: '#aaa', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                          ⚙️ DATOS DEL MAPA INTERACTIVO ACTIVOS:
                        </span>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', fontSize: '12px' }}>
                          {parsedMap.elevation_gain && <span>🔺 <b>Altimetría:</b> +{parsedMap.elevation_gain}m</span>}
                          {parsedMap.terrain && <span>🪨 <b>Terreno:</b> {parsedMap.terrain}</span>}
                          {parsedMap.est_time && <span>⏱️ <b>Tiempo Est:</b> {parsedMap.est_time}</span>}
                          {parsedMap.path && <span>📍 <b>Coordenadas:</b> {parsedMap.path.length} puntos</span>}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="admin-route-media">
                    {route.image && (
                      <a href={route.link} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={route.image} 
                          alt={`Altimetría Ruta ${index + 1}`} 
                          className="admin-route-image" 
                        />
                      </a>
                    )}
                    {route.link && (
                      <a href={route.link} target="_blank" rel="noopener noreferrer">
                        <button className="admin-route-strava-btn">
                          🏃 Ver en Strava
                        </button>
                      </a>
                    )}
                  </div>
                </div>
              );
            })
          )}
          
          <div className="admin-routes-controls">
            <button 
              onClick={toggleAdminRoutesEditing} 
              className="admin-routes-edit-btn"
            >
              ✏️ Editar Rutas
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// RENDER SPONSORS
// ==========================================
const renderSponsors = () => {
  // ✅ Si está en modo edición, mostrar GRID en lugar de carrusel
  const isEditing = isSponsorsEditing && isEditable;

  return (
    <div className="admin-sponsors-slider">
      <h2 className="admin-sponsors-title" style={{ color: '#000' }}>
        Patrocinadores
      </h2>

      {sponsorsError && (
        <div className="admin-sponsors-error">{sponsorsError}</div>
      )}

      <div className={`admin-sponsors-container ${isEditing ? 'editing-mode' : ''}`}>
        {adminSponsors.length > 0 ? (
          isEditing ? (
            // ✅ MODO EDICIÓN: GRID
            <div className="admin-sponsors-grid">
              {adminSponsors.map((sponsor, index) => (
                <div
                  key={`${sponsor}-${index}`}
                  className="admin-sponsors-grid-item"
                >
                  <img 
                    src={sponsor} 
                    alt={`Sponsor ${index + 1}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getAdminDefaultSponsor();
                    }}
                  />
                  <button
                    className="admin-sponsors-delete-btn"
                    onClick={() => handleAdminDeleteSponsor(sponsor)}
                    title="Eliminar patrocinador"
                    disabled={sponsorsLoading}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            // ✅ MODO VISUALIZACIÓN: CARRUSEL
            <div className="admin-sponsors-slider-container">
              <div className="admin-sponsors-slider-track">
                {getAdminContinuousSponsors().map((sponsor, idx) => {
                  const originalIndex = idx % adminSponsors.length;
                  const isOriginal = idx < adminSponsors.length;
                  
                  return (
                    <div
                      key={`${idx}-${sponsor}`}
                      className="admin-sponsors-item"
                    >
                      <img 
                        src={sponsor} 
                        alt={`Sponsor ${originalIndex + 1}`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = getAdminDefaultSponsor();
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )
        ) : (
          <div className="admin-sponsors-empty">
            <p>No hay patrocinadores aún.</p>
          </div>
        )}
      </div>

      <div className="admin-sponsors-controls">
        {isEditable && (
          <div className="admin-sponsors-add">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={async (e) => {
                const files = e.target.files;
                if (files.length > 0) {
                  const success = await handleAdminAddSponsors(files);
                  if (success) {
                    e.target.value = '';
                  }
                }
              }}
              id="admin-sponsor-upload"
              disabled={sponsorsLoading}
            />
            <label 
              htmlFor="admin-sponsor-upload" 
              className="admin-sponsors-add-btn"
              style={{ opacity: sponsorsLoading ? 0.5 : 1 }}
            >
              {sponsorsLoading ? '⏳ Cargando...' : '➕ Añadir Patrocinadores'}
            </label>
          </div>
        )}

        {isEditable && (
          <button
            className="admin-sponsors-edit-btn"
            onClick={toggleSponsorsEditing}
            disabled={sponsorsLoading}
          >
            {isSponsorsEditing ? '✅ Terminar Edición' : '✏️ Editar Patrocinadores'}
          </button>
        )}
      </div>

      {adminSponsors.length === 0 && !isSponsorsEditing && (
        <p className="admin-sponsors-empty-text">
          No hay patrocinadores aún. {isEditable && 'Haz clic en "Editar Patrocinadores" para agregar.'}
        </p>
      )}
    </div>
  );
};

// ==========================================
// RENDER KITS TAB
// ==========================================
const renderKitsTab = () => {
  // Solo visible para administradores
  if (!isAdmin) {
    return (
      <div className="admin-kits-tab">
        <h2 className="admin-section-title">🎽 Entrega de Kits</h2>
        <div className="admin-kits-private">
          <span className="admin-kits-lock">🔒</span>
          <h3>Acceso Restringido</h3>
          <p>Solo los administradores pueden acceder al sistema de entrega de kits.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-section-content">
      <h2 className="admin-section-title">🎽 Entrega de Kits</h2>
      <KitManagement slug={slug} />
    </div>
  );
};

  // ==========================================
  // RENDER: MENÚ LATERAL
  // ==========================================
  const renderSidebar = () => (
    <div className="admin-sidebar">
      <div className="admin-sidebar-header">
        <h2>⚙️ Panel</h2>
        <p>{slug}</p>
      </div>
      <nav className="admin-sidebar-nav">
        {menuSections.map((section) => (
          <button
            key={section.id}
            className={`admin-sidebar-btn ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            <span className="admin-sidebar-icon">{section.icon}</span>
            <span className="admin-sidebar-label">{section.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );

  // ==========================================
  // 5. RENDER LAYOUT COMPLETO
  // ==========================================
  return (
    <div className="admin-layout">
      {/* NAVBAR ADMIN */}
      <NavbarAdmin 
        adminName={adminName}
        colors={colors}
        slug={slug}
        isEditable={isAdmin}
      />

      <div className="admin-layout-container">
        {renderSidebar()}
        <div className="admin-main-content">
          <div className="admin-main-header">
          <h1>{menuSections.find(s => s.id === activeSection)?.label || 'Dashboard'}</h1>
        <h1>⚙️ Panel de Administración</h1>
        <p>Slug: {slug}</p>
      </div>
      <div className="admin-main-body">
            {renderContent()}
      
      
      {/* Footer */}
      <div className="admin-layout__footer">
        <p>© 2024 - Panel de Administración</p>
      </div>
    </div>
    </div>
      </div>
    </div>
  );
};

export default AdminLayout;